import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Thread, Message, Note, User, DashboardStats, EmailClassification } from './types';

interface ThreadState {
  threads: Thread[];
  currentThread: Thread | null;
  messages: Message[];
  notes: Note[];
  loading: boolean;
  error: string | null;
  filters: {
    status: string;
    domain: string;
    category: string;
    priority: string;
    assignee: string;
  };
  pagination: { page: number; limit: number; total: number };
  setThreads: (threads: Thread[]) => void;
  setCurrentThread: (thread: Thread | null) => void;
  setMessages: (messages: Message[]) => void;
  setNotes: (notes: Note[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setFilters: (filters: Partial<ThreadState['filters']>) => void;
  resetFilters: () => void;
  setPagination: (pagination: Partial<ThreadState['pagination']>) => void;
  addThread: (thread: Thread) => void;
  updateThread: (threadId: string, updates: Partial<Thread>) => void;
  removeThread: (threadId: string) => void;
}

const defaultFilters = {
  status: '',
  domain: '',
  category: '',
  priority: '',
  assignee: '',
};

export const useThreadStore = create<ThreadState>()(
  persist(
    (set) => ({
      threads: [],
      currentThread: null,
      messages: [],
      notes: [],
      loading: false,
      error: null,
      filters: defaultFilters,
      pagination: { page: 1, limit: 50, total: 0 },

      setThreads: (threads) => set({ threads }),
      setCurrentThread: (currentThread) => set({ currentThread }),
      setMessages: (messages) => set({ messages }),
      setNotes: (notes) => set({ notes }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
      resetFilters: () => set({ filters: defaultFilters }),
      setPagination: (pagination) => set((state) => ({ pagination: { ...state.pagination, ...pagination } })),
      addThread: (thread) => set((state) => ({ threads: [thread, ...state.threads] })),
      updateThread: (threadId, updates) =>
        set((state) => ({
          threads: state.threads.map((t) => (t.thread_id === threadId ? { ...t, ...updates } : t)),
          currentThread: state.currentThread?.thread_id === threadId ? { ...state.currentThread, ...updates } : state.currentThread,
        })),
      removeThread: (threadId) => set((state) => ({ threads: state.threads.filter((t) => t.thread_id !== threadId) })),
    }),
    {
      name: 'signal-inbox-threads',
      partialize: (state) => ({ filters: state.filters }),
    }
  )
);

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => {
        localStorage.setItem('signal_token', token);
        set({ user, token, isAuthenticated: true });
      },
      clearAuth: () => {
        localStorage.removeItem('signal_token');
        set({ user: null, token: null, isAuthenticated: false });
      },
    }),
    { name: 'signal-inbox-auth' }
  )
);

interface AIState {
  classifications: Record<string, EmailClassification>;
  summaries: Record<string, string>;
  actionItems: Record<string, string[]>;
  drafts: Record<string, string>;
  loading: Record<string, boolean>;
  setClassification: (threadId: string, classification: EmailClassification) => void;
  setSummary: (threadId: string, summary: string) => void;
  setActionItems: (threadId: string, actions: string[]) => void;
  setDraft: (threadId: string, draft: string) => void;
  setLoading: (key: string, loading: boolean) => void;
  clearThreadAI: (threadId: string) => void;
}

export const useAIStore = create<AIState>((set) => ({
  classifications: {},
  summaries: {},
  actionItems: {},
  drafts: {},
  loading: {},
  setClassification: (threadId, classification) => set((state) => ({ classifications: { ...state.classifications, [threadId]: classification } })),
  setSummary: (threadId, summary) => set((state) => ({ summaries: { ...state.summaries, [threadId]: summary } })),
  setActionItems: (threadId, actions) => set((state) => ({ actionItems: { ...state.actionItems, [threadId]: actions } })),
  setDraft: (threadId, draft) => set((state) => ({ drafts: { ...state.drafts, [threadId]: draft } })),
  setLoading: (key, loading) => set((state) => ({ loading: { ...state.loading, [key]: loading } })),
  clearThreadAI: (threadId) => set((state) => {
    const { [threadId]: _, ...classifications } = state.classifications;
    const { [threadId]: __, ...summaries } = state.summaries;
    const { [threadId]: ___, ...actionItems } = state.actionItems;
    const { [threadId]: ____, ...drafts } = state.drafts;
    return { classifications, summaries, actionItems, drafts };
  }),
}));

interface UIState {
  sidebarOpen: boolean;
  rightPanelOpen: boolean;
  rightPanelTab: 'details' | 'notes' | 'ai' | 'audit';
  composerOpen: boolean;
  composerThreadId: string | null;
  theme: 'light' | 'dark' | 'system';
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleRightPanel: () => void;
  setRightPanelTab: (tab: UIState['rightPanelTab']) => void;
  openComposer: (threadId: string) => void;
  closeComposer: () => void;
  setTheme: (theme: UIState['theme']) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      rightPanelOpen: true,
      rightPanelTab: 'details',
      composerOpen: false,
      composerThreadId: null,
      theme: 'system',
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
      toggleRightPanel: () => set((state) => ({ rightPanelOpen: !state.rightPanelOpen })),
      setRightPanelTab: (rightPanelTab) => set({ rightPanelTab }),
      openComposer: (composerThreadId) => set({ composerOpen: true, composerThreadId }),
      closeComposer: () => set({ composerOpen: false, composerThreadId: null }),
      setTheme: (theme) => set({ theme }),
    }),
    { name: 'signal-inbox-ui' }
  )
);